import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './FloatingTooltip.css';

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 12;

/** Props que el consumidor debe aplicar al elemento que dispara el tooltip. */
export interface FloatingAnchorProps {
  ref: (node: HTMLElement | null) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowOffset: number;
  isBelow: boolean;
}

interface FloatingTooltipProps {
  /** Contenido del tooltip. */
  content: ReactNode;
  /** Clase de apariencia del tooltip (fondo, tipografía, padding). */
  tooltipClassName?: string;
  /** Render-prop del ancla: recibe los handlers y el ref que debe aplicar. */
  children: (anchorProps: FloatingAnchorProps) => ReactNode;
}

function computePosition(anchorRect: DOMRect, tooltipRect: DOMRect): TooltipPosition {
  const halfWidth = tooltipRect.width / 2;
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const minCenterX = VIEWPORT_MARGIN + halfWidth;
  const maxCenterX = window.innerWidth - VIEWPORT_MARGIN - halfWidth;
  // Si el tooltip es más ancho que la ventana, minCenterX supera a maxCenterX.
  const clampedCenterX = maxCenterX < minCenterX
    ? window.innerWidth / 2
    : Math.min(Math.max(anchorCenterX, minCenterX), maxCenterX);

  const topIfAbove = anchorRect.top - ANCHOR_GAP - tooltipRect.height;
  const isBelow = topIfAbove < VIEWPORT_MARGIN;

  return {
    top: isBelow ? anchorRect.bottom + ANCHOR_GAP : topIfAbove,
    left: clampedCenterX,
    arrowOffset: anchorCenterX - clampedCenterX,
    isBelow,
  };
}

/**
 * Muestra un tooltip anclado a un elemento, renderizado en document.body.
 * Se usa cuando el ancla vive dentro de un contenedor con overflow recortado
 * (tarjetas, listas con scroll), donde un tooltip absoluto quedaría oculto.
 */
export function FloatingTooltip({ content, tooltipClassName, children }: FloatingTooltipProps) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const setAnchor = useCallback((node: HTMLElement | null) => { anchorRef.current = node; }, []);
  const openTooltip = useCallback(() => setIsOpen(true), []);
  const closeTooltip = useCallback(() => {
    setIsOpen(false);
    setPosition(null);
  }, []);

  // Se mide el tooltip ya montado (oculto) para poder anclarlo con precisión.
  useLayoutEffect(() => {
    if (!isOpen || position || !anchorRef.current || !tooltipRef.current) {
      return;
    }
    setPosition(computePosition(
      anchorRef.current.getBoundingClientRect(),
      tooltipRef.current.getBoundingClientRect(),
    ));
  }, [isOpen, position]);

  // Un tooltip fixed no sigue al ancla: al hacer scroll o redimensionar se cierra.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    window.addEventListener('scroll', closeTooltip, true);
    window.addEventListener('resize', closeTooltip);
    return () => {
      window.removeEventListener('scroll', closeTooltip, true);
      window.removeEventListener('resize', closeTooltip);
    };
  }, [isOpen, closeTooltip]);

  const anchorProps: FloatingAnchorProps = {
    ref: setAnchor,
    onMouseEnter: openTooltip,
    onMouseLeave: closeTooltip,
    onFocus: openTooltip,
    onBlur: closeTooltip,
  };

  const tooltipClasses = [
    'fp-tooltip',
    position ? '' : 'fp-tooltip--measuring',
    position?.isBelow ? 'fp-tooltip--below' : '',
    tooltipClassName ?? '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {children(anchorProps)}
      {isOpen && createPortal(
        <div
          ref={tooltipRef}
          className={tooltipClasses}
          role="tooltip"
          style={{
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            ['--fp-tooltip-arrow-x' as string]: `calc(50% + ${position?.arrowOffset ?? 0}px)`,
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}
